import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useHistory, useParams } from 'react-router-dom';
import Loader from 'react-loader-spinner';
import { FiPlus, FiSave } from 'react-icons/fi';
import { API_URLS } from '../constants/config';
import { useI18n } from '../utilities/i18n';
import { authorizedPost, getApiErrorMessage } from '../utilities/stadiumsApi';
import { useChampionships } from '../utilities/championships';
import { useAdminAccess } from '../utilities/adminAccess';
import {
  extractEntity,
  extractList,
  fromDateInputValue,
  isEnabled,
  toDateInputValue,
} from '../utilities/tournamentsApi';
import ReorderableTable from '../components/ReorderableTable';
import StadiumCrumbs from '../components/StadiumCrumbs';
import CustomModal from '../components/CustomModal';
import FlagSelect from '../components/FlagSelect';
import '../styles/createMatchModal.css';
import '../styles/stadiumsManagement.css';
import '../styles/disqualificationsManagement.css';
import '../styles/tournamentsManagement.css';

const emptyForm = {
  show: true,
  name: '',
  flag: 'eu',
  founded: '',
  socialVk: '',
};

export default function ManageCountry() {
  const { countryId } = useParams();
  const isNew = !countryId;
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useI18n();
  const { champId, loading: champsLoading } = useChampionships();
  const { canWrite } = useAdminAccess('tournaments');

  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [leagues, setLeagues] = useState([]);
  const [originalLeagues, setOriginalLeagues] = useState([]);
  const [reorderMode, setReorderMode] = useState(false);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteVisible, setDeleteVisible] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  useEffect(() => {
    if (!champId && champsLoading) return;
    initPage();
  }, [countryId, champId, champsLoading]);

  const initPage = async () => {
    setMessage({ text: '', type: '' });
    setErrors({});
    setReorderMode(false);

    if (isNew) {
      setForm(emptyForm);
      setLeagues([]);
      setOriginalLeagues([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await authorizedPost(dispatch, API_URLS.getAdminCountry, { id: Number(countryId) });
      const country = extractEntity(response.data);
      setForm({
        show: isEnabled(country.show),
        name: country.name || '',
        flag: String(country.flag || 'eu').toLowerCase(),
        founded: toDateInputValue(country.founded),
        socialVk: country.socialVk || '',
      });
      const nextLeagues = extractList(country.leagues);
      setLeagues(nextLeagues);
      setOriginalLeagues(nextLeagues);
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('stLoadError')), type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => ({ ...prev, [key]: '' }));
    }
  };

  const validate = () => {
    const nextErrors = {};
    if (!form.name.trim()) nextErrors.name = t('stdNameRequired');
    if (!form.flag) nextErrors.flag = t('trFlagRequired');
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const buildPayload = () => ({
    name: form.name.trim(),
    show: form.show ? 1 : 0,
    flag: form.flag,
    founded: fromDateInputValue(form.founded) || null,
    socialVk: form.socialVk.trim() || null,
  });

  const handleSave = async () => {
    if (!validate()) return;
    if (!champId) {
      setMessage({ text: t('championshipsLoadError'), type: 'error' });
      return;
    }

    setSaving(true);
    setMessage({ text: '', type: '' });
    try {
      const payload = buildPayload();
      if (isNew) {
        const response = await authorizedPost(dispatch, API_URLS.addCountry, {
          ...payload,
          champ_id: champId,
        });
        const created = extractEntity(response.data);
        setMessage({ text: t('trCountryCreated'), type: 'success' });
        if (created.id) {
          history.replace(`/tournaments/${created.id}`);
        } else {
          history.push('/tournaments');
        }
      } else {
        await authorizedPost(dispatch, API_URLS.changeCountry, {
          ...payload,
          id: Number(countryId),
        });
        setMessage({ text: t('stSaved'), type: 'success' });
      }
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('stSaveError')), type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await authorizedPost(dispatch, API_URLS.deleteCountry, { id: Number(countryId) });
      history.push('/tournaments');
    } catch (error) {
      setDeleteVisible(false);
      setMessage({ text: getApiErrorMessage(error, t('stdDeleteError')), type: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const handleSaveLeagueOrder = async () => {
    setSavingOrder(true);
    setMessage({ text: '', type: '' });
    try {
      await authorizedPost(dispatch, API_URLS.changeLeaguesPriority, {
        leagues: leagues.map((item, index) => ({ id: item.id, priority: index })),
      });
      const ordered = leagues.map((item, index) => ({ ...item, priority: index }));
      setLeagues(ordered);
      setOriginalLeagues(ordered);
      setReorderMode(false);
      setMessage({ text: t('stOrderSaved'), type: 'success' });
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('stOrderError')), type: 'error' });
    } finally {
      setSavingOrder(false);
    }
  };

  const leagueColumns = [
    {
      key: 'enabled',
      label: t('stStatus'),
      className: 'st-cell-status',
      render: (item) => (
        <span className={`st-badge ${isEnabled(item.enabled) ? 'enabled' : 'disabled'}`}>
          <span className="tr-btn-full">{isEnabled(item.enabled) ? t('stEnabled') : t('stDisabled')}</span>
          <span className="tr-btn-short">{isEnabled(item.enabled) ? t('stEnabledShort') : t('stDisabledShort')}</span>
        </span>
      ),
    },
    {
      key: 'name',
      label: t('stdName'),
      className: 'st-cell-name',
      render: (item) => item.name || '—',
    },
    {
      key: 'priority',
      label: t('stdPriority'),
      className: 'st-cell-priority',
      render: (item, index) => (
        <span className="st-priority-badge">{reorderMode ? index + 1 : (item.priority ?? index + 1)}</span>
      ),
    },
  ];

  const countryForm = (
    <div className="st-form-grid">
      <div className="st-field st-field-wide">
        <label className="st-check">
          <input
            type="checkbox"
            checked={form.show}
            onChange={(event) => updateField('show', event.target.checked)}
          />
          {t('stShowOnSite')}
        </label>
      </div>

      <div className="st-field">
        <label>{t('trFlag')} <span className="st-required">*</span></label>
        <FlagSelect
          value={form.flag}
          error={Boolean(errors.flag)}
          onChange={(code) => updateField('flag', code)}
        />
        {errors.flag ? <span className="st-field-error">{errors.flag}</span> : null}
      </div>

      <div className="st-field">
        <label>{t('stdName')} <span className="st-required">*</span></label>
        <input
          className={`st-input ${errors.name ? 'error' : ''}`}
          value={form.name}
          placeholder={t('trCountryNamePh')}
          onChange={(event) => updateField('name', event.target.value)}
        />
        {errors.name ? <span className="st-field-error">{errors.name}</span> : null}
      </div>

      <div className="st-field">
        <label>{t('trFounded')}</label>
        <div className="st-native-dt">
          <input
            type="date"
            className="st-input"
            value={form.founded}
            onChange={(event) => updateField('founded', event.target.value)}
          />
        </div>
      </div>

      <div className="st-field st-field-wide">
        <label>{t('trVk')}</label>
        <input
          className="st-input"
          value={form.socialVk}
          placeholder={t('trVkPh')}
          onChange={(event) => updateField('socialVk', event.target.value)}
        />
      </div>
    </div>
  );

  const leagueActions = reorderMode ? (
    <>
      <button
        type="button"
        className="st-btn st-btn-secondary"
        disabled={savingOrder}
        onClick={() => {
          setLeagues(originalLeagues);
          setReorderMode(false);
        }}
      >
        {t('stCancelReorder')}
      </button>
      <button
        type="button"
        className="st-btn st-btn-primary"
        disabled={savingOrder || !leagues.length}
        onClick={handleSaveLeagueOrder}
      >
        <FiSave /> {t('stSaveOrder')}
      </button>
    </>
  ) : (
    <>
      <button
        type="button"
        className="st-btn st-btn-secondary"
        disabled={loading || leagues.length < 2}
        onClick={() => {
          setOriginalLeagues(leagues);
          setReorderMode(true);
        }}
      >
        <span className="tr-btn-full">{t('stReorder')}</span>
        <span className="tr-btn-short">{t('stReorderShort')}</span>
      </button>
      <Link
        to={`/tournaments/${countryId}/league/new`}
        className={`st-btn st-btn-primary${loading ? ' is-disabled' : ''}`}
        aria-disabled={loading || undefined}
        onClick={(event) => { if (loading) event.preventDefault(); }}
      >
        <FiPlus />
        <span className="tr-btn-full">{t('trAddLeague')}</span>
        <span className="tr-btn-short">{t('trAddLeagueShort')}</span>
      </Link>
    </>
  );

  if (isNew) {
    return (
      <div className="layoutWrapper st-layout">
        <div className="matchCenterWrapper st-page tr-country-page">
          <div className="matchCenterHeader">
            <div className="st-header">
              <StadiumCrumbs
                items={[
                  { label: t('navTournaments'), to: '/tournaments' },
                  { label: t('trNewCountry') },
                ]}
              />
            </div>
          </div>

          <div className="st-scroll">
            <div className="st-content">
              {message.text ? <div className={`st-message ${message.type}`}>{message.text}</div> : null}
              <div className="st-block">
                {countryForm}
              </div>
            </div>
          </div>

          <div className={`st-actions ${canWrite ? 'tr-country-actions' : ''}`}>
            {canWrite ? (
            <button
              type="button"
              className="st-btn st-btn-primary"
              disabled={saving}
              onClick={handleSave}
            >
              <FiSave /> {saving ? t('trLoading') : t('trSaveCountry')}
            </button>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="layoutWrapper st-layout">
      <div className="matchCenterWrapper st-page dq-page tr-country-edit">
        <div className="matchCenterHeader">
          <div className="st-header">
            <StadiumCrumbs
              items={[
                { label: t('navTournaments'), to: '/tournaments' },
                { label: form.name.trim() || t('trEditCountry') },
              ]}
            />
          </div>
        </div>

        {message.text ? <div className={`st-message ${message.type}`}>{message.text}</div> : null}

        <div className="st-scroll dq-scroll">
          <div className="dq-split">
            <section className="st-block dq-panel">
              <h2 className="st-block-header">{t('trTournaments')}</h2>
              <div className="dq-panel-body">
                {loading ? (
                  <div className="st-loading">
                    <Loader type="Oval" color="#3ad1a0" height={48} width={48} />
                    <p>{t('trLoading')}</p>
                  </div>
                ) : (
                  <ReorderableTable
                    tableClassName="st-table-three"
                    columns={leagueColumns}
                    items={leagues}
                    getRowKey={(item) => item.id}
                    reorderMode={reorderMode}
                    onItemsChange={setLeagues}
                    getRowHref={(item) => (item.id ? `/tournaments/${countryId}/league/${item.id}` : null)}
                    empty={<div className="st-empty"><p>{t('trEmptyLeagues')}</p></div>}
                  />
                )}
                {reorderMode ? <p className="st-hint">{t('stDragHint')}</p> : null}
              </div>
              <div className="st-actions">
                {canWrite ? leagueActions : null}
              </div>
            </section>

            <section className="st-block dq-panel">
              <h2 className="st-block-header">{t('trEditCountry')}</h2>
              <div className="dq-panel-body">
                {loading ? (
                  <div className="st-loading">
                    <Loader type="Oval" color="#3ad1a0" height={48} width={48} />
                    <p>{t('trLoading')}</p>
                  </div>
                ) : countryForm}
              </div>
              <div className="st-actions">
                {canWrite ? (
                <>
                <button
                  type="button"
                  className="st-btn st-btn-danger"
                  disabled={loading || saving || deleting}
                  onClick={() => setDeleteVisible(true)}
                >
                  {t('delete')}
                </button>
                <button
                  type="button"
                  className="st-btn st-btn-primary"
                  disabled={loading || saving}
                  onClick={handleSave}
                >
                  <FiSave />
                  {saving ? t('trLoading') : (
                    <>
                      <span className="tr-btn-full">{t('stdSaveChanges')}</span>
                      <span className="tr-btn-short">{t('stdSaveChangesShort')}</span>
                    </>
                  )}
                </button>
                </>
                ) : null}
              </div>
            </section>
          </div>
        </div>
      </div>

      <CustomModal
        styles={{ width: 420, height: 'auto', paddingLeft: 32, paddingRight: 32 }}
        visible={deleteVisible}
        closeModal={() => setDeleteVisible(false)}
      >
        <div className="st-delete-modal">
          <div className="customModalHeader">{t('trDeleteCountryTitle')}</div>
          <p>{t('trDeleteCountryText')}</p>
          <div className="st-delete-actions">
            <button type="button" className="btn-modal btn-secondary-modal" onClick={() => setDeleteVisible(false)}>
              {t('cancel')}
            </button>
            <button type="button" className="btn-modal btn-danger-modal" disabled={deleting} onClick={handleDelete}>
              {t('delete')}
            </button>
          </div>
        </div>
      </CustomModal>
    </div>
  );
}
